import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, type ReactElement } from 'react';

import './i18n';

import { apiClient } from './api';
import { installResponseInterceptors } from './api/interceptors';
import { queryClient } from './api/queryClient';
import { ToastProvider } from './components/feedback';
import { ThemeProvider } from './design';
import { Router } from './routes';
import { StoreProvider } from './stores';

/**
 * App root. Provider stack (outer → inner):
 *   ThemeProvider       (MUI + CssBaseline)
 *   QueryClientProvider (TanStack Query — owns server state)
 *   ToastProvider       (MUI Snackbar bus — feeds page + interceptor toasts)
 *   StoreProvider       (MobX root — owns client/UI state)
 *   Router              (react-router-dom v6 createBrowserRouter)
 *
 * The 403/409 response interceptors are installed once on mount (the
 * 401 → refresh path lives in `api/client.ts`). i18n is side-effect-
 * imported so `t()` resolves before mount.
 */
export const App = (): ReactElement => {
  useEffect(() => {
    installResponseInterceptors(apiClient);
  }, []);

  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <StoreProvider>
            <Router />
          </StoreProvider>
        </ToastProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};
